import * as Yup from "yup";
import { Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import appConfiguration from "@/components/cfg/AppCfg";
import { useTranslation } from "react-i18next";
import useAccount from "@/modules/account/hooks/useAccount";
import schema from "./schema";
import Field from "@/components/Field";

import Wrapper from "./components/Wrapper";
import { Link } from "@/components/link";
import { useForm } from "react-hook-form";
import { useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import SubmitButton from "@/components/SubmitButton";

type Person = Yup.InferType<typeof schema>;

const SignIn = () => {
  const initialValues = useRef<Required<Person>>({ email: "", password: "" });
  const { t } = useTranslation();
  const { onLogin, account } = useAccount();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: initialValues.current,
    resolver: yupResolver(schema),
    mode: "all",
  });

  const handleSubmit = async (values: Person) => {
    try {
      await onLogin(values.email, values.password);
      navigate(appConfiguration.defaultPathAfterLogin);
    } catch (error) {
      const errorCode = error.code;
      switch (errorCode) {
        case "NotAuthorizedException":
          form.setError("email", {
            type: "manual",
            message: t("signIn.errorMessages.invalidCredentials"),
          });
          break;
        default:
          form.setError("email", {
            type: "manual",
            message: t("signIn.errorMessages.unknownError"),
          });
      }
    }
  };

  if (account) {
    return <Navigate to={appConfiguration.defaultPathAfterLogin} />;
  }

  return (
    <Wrapper>
      <Form {...form}>
        <form className="grid gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
          <Field
            name="email"
            label={t("signIn.userLabel")}
            render={({ field }) => <Input {...field} />}
          />
          <Field
            name="password"
            label={t("signIn.passwordLabel")}
            render={({ field }) => <Input {...field} type="password" />}
          />
          <Link to="/password-reset" className="text-sm ml-auto">
            {t("signIn.resetPasswordButton")}
          </Link>
          <SubmitButton
            label={t("signIn.signInButton")}
            loadingLabel={t("signIn.signingIn")}
            icon={null}
          />
        </form>
      </Form>
    </Wrapper>
  );
};

export default SignIn;
